import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';

const CONSENT_KEY = 'Petito_cookie_consent';

interface CookieConsent {
    status: 'accepted' | 'rejected';
    essentialOnly: boolean;
    decidedAt: string;
}

function tf(t: (key: string) => string, key: string, fallback: string): string {
    const value = t(key);
    return value === key ? fallback : value;
}

function readConsent(): CookieConsent | null {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as CookieConsent;
    } catch {
        return null;
    }
}

function saveConsent(status: 'accepted' | 'rejected') {
    const consent: CookieConsent = {
        status,
        essentialOnly: true,
        decidedAt: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
}

export default function CookieConsentBanner() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);

    useEffect(() => {
        setVisible(readConsent() === null);
    }, []);

    const handleAccept = () => {
        saveConsent('accepted');
        setVisible(false);
        setShowPreferences(false);
    };

    const handleReject = () => {
        saveConsent('rejected');
        setVisible(false);
        setShowPreferences(false);
    };

    if (!visible) return null;

    return (
        <>
            <div
                role="dialog"
                aria-labelledby="cookie-consent-title"
                className="fixed bottom-0 left-0 right-0 z-[60] border-t border-[var(--accent-color)] bg-[var(--bg-color)] p-4 shadow-lg safe-bottom md:p-6 max-lg:mb-[var(--mobile-bottom-nav-height)]"
            >
                <div className="container mx-auto max-w-4xl">
                    <h2 id="cookie-consent-title" className="mb-2 text-lg font-semibold text-[var(--text-color)]">
                        {tf(t, 'cookies.banner.title', 'We respect your privacy')}
                    </h2>
                    <p className="mb-4 text-sm text-[var(--text-color)] opacity-90">
                        {tf(
                            t,
                            'cookies.banner.description',
                            'Petito uses essential cookies only (authentication and security). We do not use advertising or tracking cookies. Under Belgian GDPR, you can accept or reject non-essential cookies — we currently set none beyond essentials.'
                        )}{' '}
                        <Link to="/privacy#cookies" className="font-medium text-[var(--accent-color)] underline">
                            {tf(t, 'cookies.banner.learnmore', 'Learn more')}
                        </Link>
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
                        <button
                            type="button"
                            onClick={handleAccept}
                            className="min-h-11 w-full rounded-lg bg-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-white hover:opacity-90 sm:w-auto"
                        >
                            {tf(t, 'cookies.banner.accept', 'Accept')}
                        </button>
                        <button
                            type="button"
                            onClick={handleReject}
                            className="min-h-11 w-full rounded-lg border border-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)] sm:w-auto"
                        >
                            {tf(t, 'cookies.banner.reject', 'Reject')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowPreferences(true)}
                            className="min-h-11 w-full rounded-lg px-5 py-2 text-sm font-medium text-[var(--text-color)] underline hover:opacity-80 sm:w-auto"
                        >
                            {tf(t, 'cookies.banner.manage', 'Manage preferences')}
                        </button>
                    </div>
                </div>
            </div>

            {showPreferences && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
                    <div
                        role="dialog"
                        aria-labelledby="cookie-prefs-title"
                        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-[var(--bg-color)] p-6 shadow-xl"
                    >
                        <h2 id="cookie-prefs-title" className="mb-4 text-xl font-bold text-[var(--text-color)]">
                            {tf(t, 'cookies.preferences.title', 'Cookie preferences')}
                        </h2>
                        <p className="mb-6 text-sm text-[var(--text-color)] opacity-90">
                            {tf(
                                t,
                                'cookies.preferences.intro',
                                'You can manage your cookie choices below. Petito complies with Belgian data protection law (GDPR and the Belgian Data Protection Act).'
                            )}
                        </p>

                        <div className="mb-4 rounded-xl border border-[var(--accent-color)] p-4">
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <h3 className="font-semibold text-[var(--text-color)]">
                                        {tf(t, 'cookies.preferences.essential.title', 'Essential cookies')}
                                    </h3>
                                    <p className="mt-1 text-sm text-[var(--text-color)] opacity-80">
                                        {tf(
                                            t,
                                            'cookies.preferences.essential.description',
                                            'Required for login, session management, and security. These cannot be disabled.'
                                        )}
                                    </p>
                                </div>
                                <span className="shrink-0 rounded-full bg-[var(--accent-color)] px-3 py-1 text-xs font-semibold text-white">
                                    {tf(t, 'cookies.preferences.alwayson', 'Always on')}
                                </span>
                            </div>
                        </div>

                        <p className="mb-6 text-sm text-[var(--text-color)] opacity-80">
                            {tf(
                                t,
                                'cookies.preferences.nooptional',
                                'We do not currently use optional analytics, marketing, or third-party tracking cookies.'
                            )}
                        </p>

                        <div className="flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={handleAccept}
                                className="rounded-lg bg-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
                            >
                                {tf(t, 'cookies.preferences.save', 'Save preferences')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPreferences(false)}
                                className="rounded-lg border border-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-[var(--text-color)] hover:opacity-80"
                            >
                                {tf(t, 'common.cancel', 'Cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}